import type { OpportunityType } from '@/types/Opportunity'
import type { SearchOut, Tag } from '@/types/Search'

/**
 * Route mapping for new opportunity pages
 */
const OPPORTUNITY_ROUTES: Record<OpportunityType, string> = {
  federal_contract: '/federal-contracts',
  federal_grant: '/federal-grants',
  sbir: '/sbirs',
  sled_contract: '/sled-contracts',
  dibbs_contract: '/dibbs-contracts', // legacy route
}

/**
 * Builds the href for an opportunity detail page
 *
 * @param type - The opportunity type
 * @param id - The opportunity ID
 * @returns Full path to the opportunity detail page
 */
export function buildOpportunityHref(
  type: OpportunityType,
  id: string,
): string {
  const basePath = OPPORTUNITY_ROUTES[type]
  return `${basePath}/${id}`
}

/**
 * Extracts buyer/agency/jurisdiction from opportunity tags
 *
 * Searches for tags with titles: "buyer", "agency", or "jurisdiction"
 * Returns the first matching tag's value, or null if none found.
 *
 * @param tags - Array of opportunity tags
 * @returns Buyer name or null
 */
export function extractBuyer(tags: Tag[]): string | null {
  const buyerTag = tags.find((tag) => {
    const title = tag.title.toLowerCase()
    return title === 'buyer' || title === 'agency' || title === 'jurisdiction'
  })
  return buyerTag?.value ?? null
}

/**
 * Removes markdown formatting from text
 * Used to clean AI summaries for display
 *
 * @param text - Text that may contain markdown
 * @returns Plain text without markdown formatting
 */
export function removeMarkdown(text: string): string {
  return text
    .replace(/\*\*(.+?)\*\*/g, '$1') // bold
    .replace(/\*(.+?)\*/g, '$1') // italic
    .replace(/__(.+?)__/g, '$1') // bold alt
    .replace(/_(.+?)_/g, '$1') // italic alt
    .replace(/`(.+?)`/g, '$1') // code
    .replace(/#+\s/g, '') // headers
    .replace(/\[(.+?)\]\(.+?\)/g, '$1') // links
    .replace(/^\s*[-*+]\s/gm, '') // list items
    .replace(/^\s*\d+\.\s/gm, '') // numbered lists
    .trim()
}

/**
 * Truncates text to a maximum length with ellipsis
 *
 * @param text - Text to truncate
 * @param maxLength - Maximum length
 * @returns Truncated text with ellipsis if needed
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength - 1).trim() + '…'
}
